import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useTheme } from '../../../../core/ui/ThemeContext';
import { useLanguage } from '../../../../core/i18n/LanguageContext';
import { getGlassStyles } from './ProductManagementStyles';

const SUPPORTED_LANGS = [
  { value: 'de', label: 'Deutsch (de)' },
  { value: 'en', label: 'English (en)' },
  { value: 'fr', label: 'Français (fr)' },
  { value: 'it', label: 'Italiano (it)' },
  { value: 'es', label: 'Español (es)' },
];

// Mehrsprach-Editor: verwaltet ein texts[]-Array mit { lang, name, description }.
// Sendet Aenderungen via onTextsChange(newArray).
const MultilingualTextEditor = ({ texts, onTextsChange, disabled = false }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const styles = getGlassStyles(isDarkMode);

  const addLang = () => {
    const usedLangs = texts.map((tx) => tx.lang);
    const next = SUPPORTED_LANGS.find((l) => !usedLangs.includes(l.value));
    if (!next) return;
    onTextsChange([...texts, { lang: next.value, name: '', description: '' }]);
  };

  const removeLang = (idx) => {
    // Mindestens 1 Zeile (DE) erhalten
    if (texts.length <= 1) return;
    onTextsChange(texts.filter((_, i) => i !== idx));
  };

  const updateEntry = (idx, field, value) => {
    const updated = texts.map((tx, i) => (i === idx ? { ...tx, [field]: value } : tx));
    onTextsChange(updated);
  };

  const changeLang = (idx, newLang) => {
    const updated = texts.map((tx, i) => (i === idx ? { ...tx, lang: newLang } : tx));
    onTextsChange(updated);
  };

  const usedLangs = texts.map((tx) => tx.lang);
  const canAddMore = SUPPORTED_LANGS.some((l) => !usedLangs.includes(l.value));

  return (
    <div className="space-y-4">
      {Array.isArray(texts) && texts.map((entry, idx) => {
        const availableLangs = SUPPORTED_LANGS.filter(
          (l) => l.value === entry.lang || !usedLangs.includes(l.value)
        );
        return (
          <div
            key={idx}
            className={`relative rounded-2xl p-4 ${isDarkMode ? 'bg-gray-800/30 border border-white/15' : 'bg-slate-50 border border-slate-200'}`}
          >
            <div className="flex items-center justify-between mb-3">
              {/* Sprach-Select */}
              <div className="relative">
                <select
                  value={entry.lang}
                  onChange={(e) => changeLang(idx, e.target.value)}
                  disabled={disabled}
                  className={`text-sm font-semibold px-3 py-1.5 rounded-lg border-0 bg-transparent focus:ring-2 focus:outline-none ${
                    isDarkMode
                      ? 'text-blue-300 focus:ring-blue-400/50'
                      : 'text-brand focus:ring-brand/40'
                  } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  {availableLangs.map((l) => (
                    <option
                      key={l.value}
                      value={l.value}
                      className={isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}
                    >
                      {l.label}
                    </option>
                  ))}
                </select>
              </div>
              {/* Zeile entfernen */}
              {texts.length > 1 && !disabled && (
                <button
                  type="button"
                  onClick={() => removeLang(idx)}
                  title={t('admin.productForm.texts.removeLang')}
                  className={`p-1.5 rounded-lg transition-colors ${
                    isDarkMode
                      ? 'text-red-400/70 hover:text-red-400 hover:bg-red-500/10'
                      : 'text-red-500/70 hover:text-red-600 hover:bg-red-50'
                  }`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Name */}
            <div className="relative group/input mb-3">
              <label className={`block text-xs font-semibold mb-1.5 ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
                {t('admin.productForm.texts.name')}
              </label>
              <div className="relative">
                <div className={styles.inputGlassBackground}></div>
                <input
                  type="text"
                  value={entry.name}
                  onChange={(e) => updateEntry(idx, 'name', e.target.value)}
                  disabled={disabled}
                  placeholder={t('admin.productForm.texts.namePlaceholder')}
                  className={`${styles.inputClassName} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                />
                <div className={styles.focusHighlight}></div>
              </div>
            </div>

            {/* Beschreibung */}
            <div className="relative group/input">
              <label className={`block text-xs font-semibold mb-1.5 ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
                {t('admin.productForm.texts.description')}
              </label>
              <div className="relative">
                <div className={styles.inputGlassBackground}></div>
                <textarea
                  value={entry.description}
                  onChange={(e) => updateEntry(idx, 'description', e.target.value)}
                  disabled={disabled}
                  rows={2}
                  placeholder={t('admin.productForm.texts.descriptionPlaceholder')}
                  className={`${styles.inputClassName} resize-none ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                />
                <div className={styles.focusHighlight}></div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Sprache hinzufuegen */}
      {!disabled && canAddMore && (
        <button
          type="button"
          onClick={addLang}
          className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-xl border transition-colors ${
            isDarkMode
              ? 'border-white/20 text-white/70 hover:text-white hover:border-white/40 hover:bg-white/5'
              : 'border-slate-200 text-slate-600 hover:text-brand hover:border-brand/40 hover:bg-brand/5'
          }`}
        >
          <Plus className="w-4 h-4" />
          {t('admin.productForm.texts.addLang')}
        </button>
      )}
    </div>
  );
};

export default MultilingualTextEditor;
